import { useEffect } from 'react';
// `@/app/...`, NOT `@app/...`: vite.config.ts only aliases `@`, `@shared` and
// `@features`, while tsconfig.app.json additionally declares `@app/*`. A production
// module importing via `@app/` therefore type-checks under `tsc -b` but breaks
// `vite build` with "Rollup failed to resolve import". Every production import of
// this module uses the `@/app/` form; `@app/` survives only in test files, which
// Vitest resolves from tsconfig and Rollup never bundles.
import { useAppDispatch, useAppSelector } from '@/app/store/hooks';
import { fetchDealerConfig } from '../store/dealerConfigSlice';
import { resolvePrepopulateMileage } from '../utils/resolvePrepopulateMileage';
import type { DealerConfigStatus } from '../types/dealerConfig.types';

/**
 * Resolve the ambient dealer configuration for the EFFECTIVE dealer (#789).
 *
 * Effective dealer = the dealer chosen in the quote journey, falling back to the
 * user's home dealer from the JWT `dealer_id` claim (plan D7). Derived here as a
 * selector rather than stored, so it cannot drift from `quote.selectedDealer`.
 *
 * SELF-HEALING: an `idle` effective dealer triggers a fetch. This is the path
 * that covers a mid-quote reload (the `quote` slice IS persisted so
 * selectedDealer survives; this slice is NOT, so the cache is empty) and the
 * AuthProvider refresh-on-startup path.
 *
 * SLICE A SHIPS THIS DORMANT: nothing in application code calls this hook. Its
 * only consumer is Slice B's VehicleSelectionStep.
 */
export function useDealerConfig(): {
    prePopulateMileage: boolean;
    effectiveDealerId: string | null;
    status: DealerConfigStatus;
} {
    const dispatch = useAppDispatch();
    const selectedDealerId = useAppSelector((s) => s.quote.selectedDealer?.id ?? null);
    // From the AUTH slice, not this one: authSlice is written by every auth path
    // (login, useAuth refresh, AuthProvider startup refresh, scheduled refresh),
    // so this survives a page reload. See plan D8 / G24.
    const homeDealerId = useAppSelector((s) => s.auth.dealerId);
    const effectiveDealerId = selectedDealerId ?? homeDealerId;

    const settings = useAppSelector((s) =>
        (effectiveDealerId ? s.dealerConfig.byDealerId[effectiveDealerId] : undefined));
    const status = useAppSelector((s) =>
        (effectiveDealerId ? s.dealerConfig.status[effectiveDealerId] : undefined)) ?? 'idle';

    useEffect(() => {
        // Both-absent is a first-class state, not an error: no dealer selected AND
        // no home dealer (users.dealer_id is nullable). Fire nothing — never issue
        // GET /dealers/null. Plan D7.
        if (effectiveDealerId && status === 'idle') {
            void dispatch(fetchDealerConfig(effectiveDealerId));
        }
    }, [dispatch, effectiveDealerId, status]);

    return {
        prePopulateMileage: resolvePrepopulateMileage(settings),
        effectiveDealerId,
        status,
    };
}
